<template>
  <ModalVue
    @close="handleClose"
    :modal-active="modalActive"
    :has-footer-border="false"
    maxWidth="max-w-[721px]"
  >
    <template #header>
      <div class="flex">
        <h2 class="text-lg sm:text-lg font-bold text-gray-800 truncate max-w-xl">
          {{ unitData?.name }} - Configuração de visualização
        </h2>
      </div>
    </template>

    <transition-group tag="div" name="list" class="flex flex-col gap-3 my-[-12px]">
      <div
        v-for="(scale, scaleIndex) in sortedScales"
        :key="scale.id"
        class="bg-[#F0F0F4] rounded-lg transition-shadow"
        :class="{
          'shadow-lg': draggedItem?.type === 'scale' && draggedItem.scaleIndex === scaleIndex
        }"
      >
        <div
          class="flex items-center justify-between p-2 cursor-move transition-colors duration-300 ease-in-out rounded-t-lg"
          :class="{ 'rounded-b-lg': !isScaleOpen(scale.id) }"
          draggable="true"
          @dragstart.stop="onDragStart('scale', { scaleIndex })"
          @dragend="onDragEnd"
          @dragover.prevent
          @drop.stop="onDrop('scale', { scaleIndex })"
        >
          <div class="flex items-center">
            <v-icon :icon="mdiDragVertical" class="text-gray-500"></v-icon>
            <span class="text-black ml-2">{{ scale.name }}</span>
          </div>
          <button
            @click="toggleAccordion(scale.id, 'scale')"
            class="p-1 rounded-full hover:bg-gray-200"
          >
            <v-icon
              :icon="mdiChevronDown"
              class="transition-transform"
              :class="{ 'rotate-180': isScaleOpen(scale.id) }"
            ></v-icon>
          </button>
        </div>

        <div v-if="isScaleOpen(scale.id)" class="p-2">
          <transition-group tag="div" name="list" class="flex flex-col gap-2">
            <div
              v-for="(shift, shiftIndex) in scale.shifts"
              :key="shift.id"
              class="bg-white rounded-lg transition-shadow"
              :class="{
                'shadow-md':
                  draggedItem?.type === 'shift' &&
                  draggedItem.scaleIndex === scaleIndex &&
                  draggedItem.shiftIndex === shiftIndex
              }"
            >
              <div
                class="flex items-center justify-between p-2 cursor-move"
                :class="{ 'rounded-b-lg': !isShiftOpen(shift.id) }"
                draggable="true"
                @dragstart.stop="onDragStart('shift', { scaleIndex, shiftIndex })"
                @dragend="onDragEnd"
                @dragover.prevent
                @drop.stop="onDrop('shift', { scaleIndex, shiftIndex })"
              >
                <div class="flex items-center">
                  <v-icon :icon="mdiDragVertical" class="text-gray-400"></v-icon>
                  <span class="text-black ml-2">{{ shift.name }}</span>
                </div>
                <button
                  @click="toggleAccordion(shift.id, 'shift')"
                  class="p-1 rounded-full hover:bg-gray-100"
                >
                  <v-icon
                    :icon="mdiChevronDown"
                    class="transition-transform"
                    :class="{ 'rotate-180': isShiftOpen(shift.id) }"
                  ></v-icon>
                </button>
              </div>

              <div v-if="isShiftOpen(shift.id)" class="p-2">
                <transition-group tag="div" name="list" class="flex flex-col gap-2">
                  <div
                    v-for="(position, positionIndex) in shift.positions"
                    :key="position.id"
                    class="flex items-center p-3 bg-[#F0F0F4] rounded-md cursor-move"
                    :class="{
                      'opacity-50':
                        draggedItem?.type === 'position' &&
                        draggedItem.scaleIndex === scaleIndex &&
                        draggedItem.shiftIndex === shiftIndex &&
                        draggedItem.positionIndex === positionIndex
                    }"
                    draggable="true"
                    @dragstart.stop="
                      onDragStart('position', { scaleIndex, shiftIndex, positionIndex })
                    "
                    @dragend="onDragEnd"
                    @dragover.prevent
                    @drop.stop="onDrop('position', { scaleIndex, shiftIndex, positionIndex })"
                  >
                    <v-icon :icon="mdiDragVertical" class="text-gray-400"></v-icon>
                    <span class="text-black ml-2">{{ position.name }}</span>
                  </div>
                </transition-group>
                <p
                  v-if="!shift.positions || !shift.positions.length"
                  class="text-sm text-gray-500 pt-2"
                >
                  Nenhuma posição neste turno.
                </p>
              </div>
            </div>
          </transition-group>
          <p v-if="!scale.shifts || !scale.shifts.length" class="text-sm text-gray-500 pt-2">
            Nenhum turno nesta escala.
          </p>
        </div>
      </div>
    </transition-group>

    <template #footer>
      <div class="flex justify-between gap-4">
        <outline-button text="Cancelar" width="95px" height="48px" @click="handleClose" />
        <filled-button
          text="Salvar"
          width="76px"
          height="48px"
          @click="handleSave"
          :disabled="isSaving"
          :loading="isSaving"
        />
      </div>
    </template>
  </ModalVue>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'
import { mdiDragVertical, mdiChevronDown } from '@mdi/js'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { useToast } from 'vue-toastification'
import ScheduleService from '@/Network/Services/ScheduleUnitService'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'
import { useErrorToast } from '@/composables/useErrorToast'
import { UpdateVisualizationOrderPayload } from '@/Network/Types/Requests/ScheduleRequests'

const toast = useToast()
const { notifyError } = useErrorToast()
const scheduleService = new ScheduleService()

interface Position {
  id: number | string
  name: string
}

interface Shift {
  id: number | string
  name: string
  positions: Position[]
}

export interface Scale {
  id: number | string
  name: string
  shifts: Shift[]
}

export interface UnitData {
  id: number | string
  name: string
  schedules: Scale[]
}

type DraggedItem = {
  type: 'scale' | 'shift' | 'position'
  scaleIndex: number
  shiftIndex?: number
  positionIndex?: number
}

const props = defineProps<{
  modalActive: boolean
  unitData: UnitData | null
}>()

const emits = defineEmits<{
  (e: 'close'): void
  (e: 'save', scales: Scale[]): void
}>()

const sortedScales = ref<Scale[]>([])
const openScales = ref<Set<number | string>>(new Set())
const openShifts = ref<Set<number | string>>(new Set())
const draggedItem = ref<DraggedItem | null>(null)
const isSaving = ref(false)

const scheduleStore = useScheduleStore()

watch(
  () => props.unitData,
  (newUnitData) => {
    sortedScales.value = JSON.parse(JSON.stringify(newUnitData?.schedules || []))
  },
  { immediate: true, deep: true }
)

const toggleAccordion = (id: number | string, type: 'scale' | 'shift') => {
  const targetSet = type === 'scale' ? openScales.value : openShifts.value
  targetSet.has(id) ? targetSet.delete(id) : targetSet.add(id)
}
const isScaleOpen = (id: number | string) => openScales.value.has(id)
const isShiftOpen = (id: number | string) => openShifts.value.has(id)

const onDragStart = (type: DraggedItem['type'], indices: Omit<DraggedItem, 'type'>) => {
  draggedItem.value = { type, ...indices }
}

const onDragEnd = () => {
  draggedItem.value = null
}

const onDrop = (dropType: DraggedItem['type'], dropIndices: Omit<DraggedItem, 'type'>) => {
  if (!draggedItem.value) return
  const { type: dragType, ...dragIndices } = draggedItem.value

  if (dragType === 'scale' && dropType === 'scale') {
    const item = sortedScales.value.splice(dragIndices.scaleIndex, 1)[0]
    sortedScales.value.splice(dropIndices.scaleIndex, 0, item)
  }

  if (dragType === 'shift' && dropType === 'shift') {
    const fromScale = sortedScales.value[dragIndices.scaleIndex]
    const item = fromScale.shifts.splice(dragIndices.shiftIndex!, 1)[0]
    const toScale = sortedScales.value[dropIndices.scaleIndex]
    toScale.shifts.splice(dropIndices.shiftIndex!, 0, item)
  }

  if (dragType === 'position' && dropType === 'position') {
    const fromShift = sortedScales.value[dragIndices.scaleIndex].shifts[dragIndices.shiftIndex!]
    const item = fromShift.positions.splice(dragIndices.positionIndex!, 1)[0]
    const toShift = sortedScales.value[dropIndices.scaleIndex].shifts[dropIndices.shiftIndex!]
    toShift.positions.splice(dropIndices.positionIndex!, 0, item)
  }
}

const handleClose = () => emits('close')

const handleSave = async () => {
  if (isSaving.value || !props.unitData) return
  isSaving.value = true

  const schedulesPayload: UpdateVisualizationOrderPayload = sortedScales.value.map((scale) => {
    const finalOrder: { shift_id: number; position_id: number }[] = []
    scale.shifts.forEach((shift) => {
      shift.positions.forEach((position) => {
        finalOrder.push({
          shift_id: Number(shift.id),
          position_id: Number(position.id)
        })
      })
    })

    return {
      schedule_id: Number(scale.id),
      order: finalOrder
    }
  })

  const finalPayload = {
    schedules: schedulesPayload
  }

  try {
    await scheduleService.updateVisualizationOrder(Number(props.unitData.id), finalPayload)
    // Refetch isolado: a ordenação já foi persistida — falha do reload não pode
    // cair no catch abaixo e virar um erro falso de ordenação.
    await scheduleStore
      .getSchedulesForPage()
      .catch((error) => notifyError(error, 'Não foi possível recarregar as escalas'))

    toast.success('Configuração de visualização efetuada com sucesso!')
    handleClose()
  } catch (error) {
    console.error('Error saving the order:', error)
    notifyError(error, 'Não foi possível salvar a ordenação das escalas.')
  } finally {
    isSaving.value = false
  }
}
</script>

<style>
.list-move {
  transition: transform 0.3s ease;
}
.list-enter-active,
.list-leave-active {
  transition: all 0.3s ease;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
.rotate-180 {
  transform: rotate(180deg);
}
</style>
